import { useState, useCallback } from 'react';
import {
    tenantLoginUrl,
    tenantLoginPath,
    tenantLoginShareMessage,
    tenantLoginBaseUrl,
} from '../utils/tenantLoginUrl.js';
import './TenantLoginLink.css';

async function copyText(text) {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
}

/**
 * Enlace de login de una empresa: copiar URL o mensaje para el cliente.
 */
export default function TenantLoginLink({ slug, name, loginUrl, compact = false }) {
    const [copied, setCopied] = useState(null);
    const url = loginUrl || tenantLoginUrl(slug);
    const path = tenantLoginPath(slug);
    const canonicalBase = (import.meta.env.VITE_PUBLIC_APP_URL || '').trim();

    const flash = useCallback((key) => {
        setCopied(key);
        window.setTimeout(() => setCopied(null), 2200);
    }, []);

    async function onCopyUrl() {
        try {
            await copyText(url);
            flash('url');
        } catch {
            /* ignore */
        }
    }

    async function onCopyMessage() {
        try {
            await copyText(tenantLoginShareMessage({ name, slug, url }));
            flash('msg');
        } catch {
            /* ignore */
        }
    }

    if (compact) {
        return (
            <div className="tenant-login-link tenant-login-link-compact">
                <code className="tenant-login-link-path" title={url}>
                    {path}
                </code>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => void onCopyUrl()}>
                    {copied === 'url' ? 'Copiado' : 'Copiar'}
                </button>
            </div>
        );
    }

    return (
        <div className="tenant-login-link">
            <p className="tenant-login-link-label">Enlace de acceso para personal</p>
            <div className="tenant-login-link-url-row">
                <input
                    className="input tenant-login-link-input"
                    type="text"
                    readOnly
                    value={url}
                    aria-label={`Enlace de acceso ${name || slug}`}
                    onFocus={(e) => e.target.select()}
                />
            </div>
            {canonicalBase &&
                typeof window !== 'undefined' &&
                tenantLoginBaseUrl() !== window.location.origin.replace(/\/$/, '') && (
                <p className="tenant-login-link-env-hint text-xs text-muted">
                    Enlace para enviar al cliente (configurado en VITE_PUBLIC_APP_URL). Usted puede estar en otro
                    entorno.
                </p>
            )}
            <div className="tenant-login-link-actions">
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void onCopyUrl()}>
                    {copied === 'url' ? '✓ Enlace copiado' : 'Copiar enlace'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => void onCopyMessage()}>
                    {copied === 'msg' ? '✓ Mensaje copiado' : 'Copiar mensaje para enviar'}
                </button>
                <a
                    className="btn btn-secondary btn-sm"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Abrir
                </a>
            </div>
        </div>
    );
}
